import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { ticketKeys } from './keys'
import type { TicketActivityResponse, TicketDetail, TicketListResponse } from '@/types/ticket'

// asUser must live in the query key: without it a lensed and an unlensed view
// of the same params would share a cache entry and serve the wrong person's
// tickets after switching the lens on or off.
export function useTickets(params: Record<string, unknown> = {}, asUser?: string) {
  return useQuery({
    queryKey: [...ticketKeys.list(params), asUser ?? null],
    queryFn: () => api<TicketListResponse>('/support-tickets', {
      query: asUser ? { ...params, asUserId: asUser } : params,
    }),
    // The admin queue and inbox are left open for long stretches; focus
    // refetch alone misses tickets that arrive while the tab stays in view.
    refetchInterval: 30_000,
  })
}

// `poll: false` while the ticket's live stream is up (useTicketEvents): its events refetch instead, and polling
// takes over again whenever the stream drops.
export function useTicket(id: string, asUser?: string, { poll = true }: { poll?: boolean } = {}) {
  return useQuery({
    queryKey: [...ticketKeys.detail(id), asUser ?? null],
    queryFn: () => api<TicketDetail>(`/support-tickets/${id}`, {
      query: asUser ? { asUserId: asUser } : undefined,
    }),
    refetchInterval: poll ? 30_000 : false,
  })
}

export interface TicketCompany {
  _id: string
  name?: string
}

// Companies that have tickets — feeds the admin queue's company filter.
export function useTicketCompanies(enabled = true) {
  return useQuery({
    queryKey: [...ticketKeys.all, 'companies'],
    queryFn: () => api<{ data: TicketCompany[] }>('/support-tickets/companies'),
    staleTime: 5 * 60_000,
    enabled,
  })
}

export interface TicketActivityParams {
  minutes?: number
  direction?: 'before' | 'both'
  types?: string[]
}

export function useTicketActivity(id: string, params: TicketActivityParams, enabled = true) {
  const query: Record<string, string | number> = {
    minutes: params.minutes ?? 60,
    direction: params.direction ?? 'before',
  }
  if (params.types?.length) query.types = params.types.join(',')

  return useQuery({
    queryKey: ticketKeys.activity(id, query),
    queryFn: () => api<TicketActivityResponse>(`/support-tickets/${id}/activity`, { query }),
    enabled,
  })
}

export interface Reporter {
  _id: string
  name: string
  email: string
  companyId: string | null
  companyName: string
}

export function useReporters() {
  return useQuery({
    queryKey: ticketKeys.reporters(),
    queryFn: () => api<{ data: Reporter[] }>('/support-tickets/reporters'),
    select: res => res.data,
    staleTime: 5 * 60_000,
  })
}
